import { APP_VERSION_INFO } from '../config/version';

export interface ReleaseManifest {
  version: string;
  commitHash: string;
  buildTime: string;
  releaseUrl: string;
}

const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const COMMIT_HASH = /^(?:[0-9a-f]{7}|dev)$/i;
const RELEASE_URL = /^https:\/\/github\.com\/judasane\/zunelo\/releases\/tag\/v\d+\.\d+\.\d+$/;

function parseVersion(value: string): [number, number, number] | undefined {
  const match = SEMVER.exec(value);
  if (!match) return undefined;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function isNewerVersion(remote: string, current: string): boolean {
  const next = parseVersion(remote);
  const loaded = parseVersion(current);
  if (!next || !loaded) return false;
  for (let index = 0; index < next.length; index += 1) {
    if (next[index] !== loaded[index]) return next[index] > loaded[index];
  }
  return false;
}

function isReleaseManifest(value: unknown): value is ReleaseManifest {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ReleaseManifest>;
  return Boolean(
    typeof candidate.version === 'string' &&
    parseVersion(candidate.version) &&
    typeof candidate.commitHash === 'string' && COMMIT_HASH.test(candidate.commitHash) &&
    typeof candidate.buildTime === 'string' && !Number.isNaN(Date.parse(candidate.buildTime)) &&
    typeof candidate.releaseUrl === 'string' && RELEASE_URL.test(candidate.releaseUrl),
  );
}

export async function fetchNewerRelease(
  fetchImpl: typeof fetch = fetch,
  currentVersion: string = APP_VERSION_INFO.version,
  signal?: AbortSignal,
): Promise<ReleaseManifest | undefined> {
  try {
    const response = await fetchImpl(`/release-manifest.json?check=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) return undefined;
    const payload: unknown = await response.json();
    return isReleaseManifest(payload) && isNewerVersion(payload.version, currentVersion)
      ? payload
      : undefined;
  } catch {
    return undefined;
  }
}
